import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { UIContext } from "./UIProvider";
import { downloadReport, handleApiError } from "../lib/utils";
import { LinkedAccountsContext } from "./LinkedAccountsProvider";
import { endOfMonth, startOfMonth } from "date-fns";
import {
  applyAndConfirmTransactionApi,
  ApplyAndConfirmTransactionDataPayload,
  applyTransactionApi,
  bulkConfirmTransactionApi,
  Categories,
  confirmTransactionApi,
  excludeTransactionApi,
  fetchTransactionsApi,
  getCategoriesApi,
  getCategoriesCountes,
  getCustomerVendorOptions,
  getTransactionRecordTypeOptions,
  getTransactionTypeOptions,
  Transaction,
  undoCategorizedTransactionApi,
} from "../pages/Banking/components/BankingApis";

interface Options {
  label: string;
  value: string;
  other?: string;
  category?: string;
}
export type FetchTransactionsParams = {
  accountId?: string;
  category?: number | null;
  term?: string;
  toDate?: Date | null;
  fromDate?: Date | null;
  pageNumber?: number;
  pageSize?: number;
};

export type FetchCountParams = {
  accountId?: string;
  toDate?: Date | null;
  fromDate?: Date | null;
};
interface CategoriesCountData {
  recordCount: number;
  recordType: number;
}
export type BankingContextType = {
  categories: Categories[];
  transactions: Transaction[];
  transactionsLoading?: boolean;
  selectedCategory: number | null;
  setSelectedCategory: (category: number) => void;
  searchQuery: string;
  handleSearch: (query: string) => void;
  handleSearchClick: () => void;
  handleDateChange?: (fromDate: Date, toDate: Date) => void;
  confirmTransaction: (selectedId: string) => Promise<void>;
  isEditTransactionModalOpen?: boolean;
  handleChangeEditTransactionModal: (
    isOpen: boolean,
    selectedRows?: Transaction[]
  ) => void;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: (rowSelection: Record<string, boolean>) => void;
  loading?: {
    isConfirming: boolean;
    customerSearchLoading: boolean;
    loadingCategories: boolean;
    loadingReportType: boolean;
    loadingTransactionType: boolean;
    isExludingTransaction: boolean;
    isbulkConfirmingTransaction: boolean;
    isundoCategorizedTransaction: boolean;
    isApplyingTransaction: boolean;
    trnasactionsLoading: boolean;
    isDownloading: boolean;
    isMatching: boolean;
  };
  transactionTypeOptions?: Options[];
  categoryOptions?: { label: string; options: Options[] }[];
  customerVendorOptions?: Options[]; // Adding customerVendorOptions to the context
  handleCustomerInputChange?: (input: string) => void;
  excludeTransaction?: (selectedIds: Transaction[]) => Promise<void>;
  bulkConfirmTransaction?: (selectedrowIds: Transaction[]) => Promise<void>;
  undoCateogrizedTransaction?: (selectedId: string) => Promise<void>;
  handleApply?: (
    type: string,
    data: ApplyAndConfirmTransactionDataPayload
  ) => void;
  handlePageChange?: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  pagination?: {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
    totalRecords: number;
  };
  handleDownloadTransactions: () => void;
  editTransaction: Transaction | null;
  // In BankingContextType
  fetchTransactions: (params: FetchTransactionsParams) => Promise<void>;
  modalStates: {
    [key: string]: boolean;
  };
  handleMatchTransaction: (
    isOpenMatch: boolean,
    selectedRows: Transaction[]
  ) => void;
  categoriesCount: CategoriesCountData[];
  getCategoriesCount: (params: FetchCountParams) => Promise<void>;
};

export const BankingContext = createContext<BankingContextType>({
  categories: [],
  transactions: [],
  transactionsLoading: false,
  selectedCategory: null,
  setSelectedCategory: () => {},
  searchQuery: "",
  handleSearch: () => {},
  handleSearchClick: () => {},
  handleDateChange: () => {},
  confirmTransaction: async () => {},
  isEditTransactionModalOpen: false,
  handleChangeEditTransactionModal: () => {},
  rowSelection: {},
  setRowSelection: () => {},
  loading: {
    isConfirming: false,
    customerSearchLoading: false,
    loadingCategories: false,
    loadingReportType: false,
    loadingTransactionType: false,
    isExludingTransaction: false,
    isbulkConfirmingTransaction: false,
    isundoCategorizedTransaction: false,
    isApplyingTransaction: false,
    trnasactionsLoading: false,
    isDownloading: false,
    isMatching: false,
  },
  transactionTypeOptions: [],
  categoryOptions: [],
  customerVendorOptions: [],
  handleCustomerInputChange: () => {},
  excludeTransaction: async () => {},
  bulkConfirmTransaction: async () => {},
  undoCateogrizedTransaction: async () => {},
  handleApply: () => {},
  handlePageChange: () => {},
  handlePageSizeChange: () => {},
  pagination: {
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  handleDownloadTransactions: () => {},
  editTransaction: null,
  fetchTransactions: async () => {},
  modalStates: {
    isMatchTransactionModalOpen: false,
    isEditTransactionModalOpen: false,
  },
  handleMatchTransaction: () => {},
  categoriesCount: [],
  getCategoriesCount: async () => {},
});

type Props = PropsWithChildren;

export const BankingProvider = ({ children }: Props) => {
  const { selectedAccountId } = useContext(LinkedAccountsContext);
  const [isLoading, setIsLoading] = useState({
    isConfirming: false,
    customerSearchLoading: false,
    loadingCategories: false,
    isExludingTransaction: false,
    isbulkConfirmingTransaction: false,
    isundoCategorizedTransaction: false,
    isApplyingTransaction: false,
    trnasactionsLoading: false,
    loadingReportType: false,
    loadingTransactionType: false,
    isDownloading: false,
    isMatching: false,
  });

  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [categories, setCategories] = useState<Categories[]>([]);
  const [categoriesCount, setCategoriesCount] = useState<CategoriesCountData[]>(
    []
  );
  // state for tab selection
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // state for handling table row selection
  const [rowSelection, setRowSelection] = useState({});
  const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] =
    useState<boolean>(false);

  const [modalStates, setModalStates] = useState({
    isEditTransactionModalOpen: false,
    isMatchTransactionModalOpen: false,
  });
  const [transactionTypeOptions, setTransactionTypeOptions] = useState<
    Options[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; options: Options[] }[]
  >([]);
  const [customerVendorOptions, setCustomerVendorOptions] = useState<Options[]>(
    []
  );

  // filteration of date
  const [transactionDates, setTransactionDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  // state for handling form reset on edit modal
  const [editTransaction, setEditTransaction] = useState<Transaction | null>(
    null
  );

  const handleMatchTransaction = async (
    isOpenMatch: boolean,
    selectedRows?: Transaction[]
  ) => {
    setModalStates({
      ...modalStates,
      isMatchTransactionModalOpen: isOpenMatch,
    });

    if (isOpenMatch) {
      if (selectedRows) {
        setEditTransaction(selectedRows[0]);
      }
    } else {
      setEditTransaction(null);
    }
  };
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const fetchTransactions = async ({
    accountId,
    category,
    term,
    toDate,
    fromDate,
    pageNumber,
    pageSize,
  }: {
    accountId?: string;
    category?: number | null;
    term?: string;
    toDate?: Date | null;
    fromDate?: Date | null;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    setIsLoading((prev) => ({ ...prev, trnasactionsLoading: true }));
    try {
      // Use fallback from state if no parameter provided
      const _accountId = accountId ?? selectedAccountId ?? "";
      const _category = category ?? selectedCategory;
      const _term = term ?? searchQuery;
      const _fromDate = fromDate ?? transactionDates.startDate;
      const _toDate = toDate ?? transactionDates.endDate;
      const _pageNumber = pageNumber ?? pagination.pageNumber;
      const _pageSize = pageSize ?? pagination.pageSize;
      const response = await fetchTransactionsApi({
        businessId,
        accountId: _accountId,
        category: _category,
        term: _term,
        toDate: _toDate,
        fromDate: _fromDate,
        pageNumber: _pageNumber,
        pageSize: _pageSize,
      });
      if (response && response.success && response.data) {
        setTransactions(response.data.items || []);
        setPagination({
          ...pagination,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
          pageSize: response.data.pageSize,
          pageNumber: response.data.pageNumber,
        });
        onSuccess?.(
          "GET",
          "Transactions",
          response.message || "Transactions loaded!"
        );
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching transactions."));
    } finally {
      setTimeout(() => {
        setIsLoading((prev) => ({ ...prev, trnasactionsLoading: false }));
      }, 200);
      setRowSelection({});
    }
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, pageNumber: page });
    fetchTransactions({ pageNumber: page });
  };

  const handlePageSizeChange = (size: number) => {
    setPagination({ ...pagination, pageSize: size, pageNumber: 1 });
    fetchTransactions({ pageSize: size });
  };

  useEffect(() => {
    setTransactionDates((prevDates) => ({
      startDate: prevDates.startDate ?? startOfMonth(new Date()),
      endDate: prevDates.endDate ?? endOfMonth(new Date()),
    }));
    if (
      selectedAccountId &&
      categories.length > 0 &&
      selectedCategory // make sure a selectedCategory is also present
    ) {
      getCategoriesCount({
        accountId: selectedAccountId,
        fromDate: transactionDates.startDate,
        toDate: transactionDates.endDate,
      });
      fetchTransactions({
        accountId: selectedAccountId ?? undefined,
        category: selectedCategory,
        term: "",
        fromDate: transactionDates.startDate,
        toDate: transactionDates.endDate,
      });
    }
  }, [selectedAccountId, categories, selectedCategory]);

  const getCategoriesCount = async ({
    accountId,
    fromDate,
    toDate,
  }: {
    accountId?: string;
    fromDate?: Date | null;
    toDate?: Date | null;
  }) => {
    // Ensure queryParams is correctly formatted
    accountId = accountId ?? selectedAccountId ?? ""; // Set accountId to the selectedAccountId if provided
    fromDate = fromDate ?? transactionDates.startDate; // Use provided fromDate, else fallback to startDate
    toDate = toDate ?? transactionDates.endDate; // Use provided toDate, else fallback to endDate

    // Now call the API function with the correct parameters
    let response = await getCategoriesCountes(
      businessId,
      accountId,
      fromDate,
      toDate
    );

    if (response && response.success && response.data) {
      setCategoriesCount(response.data); // Set state with the response data
    } else {
    }
  };

  useEffect(() => {
    const getCategories = async () => {
      setIsLoading((prev) => ({ ...prev, loadingCategories: true }));
      try {
        const categories = await getCategoriesApi(businessId);
        setCategories(categories);
        const firstCategoryId = categories?.[0]?.masterId;
        if (!selectedCategory || selectedCategory == null) {
          setSelectedCategory(firstCategoryId);
        }
        onSuccess?.("OPTIONS", "getCategories", "Data retrieved successfully!");
      } catch (error) {
        onError?.(new Error("An error occurred while fetching Customers."));
      } finally {
        setIsLoading((prev) => ({ ...prev, loadingCategories: false }));
      }
    };
    if (selectedAccountId) {
      getCategories();
    }
  }, [selectedAccountId]);

  const confirmTransaction = async (selectedId: string) => {
    setIsLoading((prev) => ({ ...prev, isConfirming: true }));
    try {
      const response = await confirmTransactionApi(businessId, selectedId);
      if (response && response.success) {
        onSuccess?.(
          "POST",
          "confirmTransaction",
          response.message || "Transaction confirmed successfully!"
        );
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while confirming Transaction."));
    } finally {
      setRowSelection({});
      getCategoriesCount({});
      fetchTransactions({});
      setTimeout(() => {
        setIsLoading((prev) => ({ ...prev, isConfirming: false }));
      }, 200);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setIsLoading((prev) => ({ ...prev, trnasactionsLoading: true }));
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    // Debounce API call
    searchTimeout.current = setTimeout(async () => {
      fetchTransactions({
        term: query,
      });
      setIsLoading((prev) => ({ ...prev, trnasactionsLoading: false }));
    }, 400);
  };

  const handleSearchClick = () => {
    fetchTransactions({
      accountId: selectedAccountId ?? undefined,
      category: selectedCategory,
      term: searchQuery,
    });
  };

  const handleDateChange = (fromDate: Date, toDate: Date) => {
    setTransactionDates({
      startDate: fromDate,
      endDate: toDate,
    });
    getCategoriesCount({
      accountId: selectedAccountId || undefined,
      fromDate,
      toDate,
    });
    fetchTransactions({
      accountId: selectedAccountId ?? undefined,
      category: selectedCategory,
      term: searchQuery,
      fromDate: fromDate,
      toDate: toDate,
    });
  };

  const handleChangeEditTransactionModal = (
    isOpen: boolean,
    selectedRows?: Transaction[]
  ) => {
    setIsEditTransactionModalOpen(isOpen);

    if (isOpen) {
      if (selectedRows) {
        setEditTransaction(selectedRows[0]);
      }
      getTransactionType();
      getTransactionRecordType();
      getCustomerVendor();
    }
  };

  const getTransactionType = async () => {
    setIsLoading((prev) => ({ ...prev, loadingTransactionType: true }));
    try {
      const options = await getTransactionTypeOptions(businessId);
      setTransactionTypeOptions(options);
      onSuccess?.(
        "OPTIONS",
        "getTransactionType",
        "Transaction type retrieved successfully!"
      );
    } catch (error) {
      onError?.(
        new Error("An error occurred while retrieving Transaction Type.")
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, loadingTransactionType: false }));
    }
  };

  const getTransactionRecordType = async () => {
    setIsLoading((prev) => ({ ...prev, loadingReportType: true }));
    try {
      const options = await getTransactionRecordTypeOptions(businessId);
      const groupedOptions = options.reduce((acc: any, option) => {
        const category = option.category || "Other";
        if (!acc[category]) acc[category] = [];
        acc[category].push(option);
        return acc;
      }, {});

      const formattedOptions = Object.keys(groupedOptions).map((category) => ({
        label: category,
        options: groupedOptions[category],
      }));
      setCategoryOptions(formattedOptions);
      onSuccess?.(
        "OPTIONS",
        "getTransactionRecordType",
        "Transaction Record type retrieved successfully!"
      );
    } catch (error) {
      onError?.(
        new Error("An error occurred while retrieving Transaction Record Type.")
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, loadingReportType: false }));
      // setLoadingCategories(false);
    }
  };

  const getCustomerVendor = async () => {
    setIsLoading((prev) => ({ ...prev, customerSearchLoading: true }));
    try {
      const options = await getCustomerVendorOptions(businessId, "");
      onSuccess?.(
        "OPTIONS",
        "getCustomerVendor",
        "Data retrieved successfully!"
      );
      setCustomerVendorOptions(options);
    } catch (error) {
      onError?.(
        new Error("An error occurred while Retrieving Transaction Record Type.")
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, customerSearchLoading: false }));
    }
  };

  const handleCustomerInputChange = (input: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setIsLoading((prev) => ({ ...prev, customerSearchLoading: true }));
    // Debounce API call
    searchTimeout.current = setTimeout(async () => {
      const options = await getCustomerVendorOptions(businessId, input);
      setCustomerVendorOptions(options);
      setIsLoading((prev) => ({ ...prev, customerSearchLoading: false }));
    }, 400);
  };

  const applyAndConfirmTransaction = async (
    data: ApplyAndConfirmTransactionDataPayload
  ) => {
    setIsLoading((prev) => ({ ...prev, isConfirming: true }));
    try {
      const response = await applyAndConfirmTransactionApi(businessId, data);
      if (response && response.success) {
        onSuccess?.(
          "POST",
          "applyAndConfirmTransaction",
          "Transaction Applied Successfully"
        );
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(
        new Error("An error occurred while apply & confirm Transaction.")
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, isConfirming: false }));
      getCategoriesCount({});
      fetchTransactions({});
      setIsEditTransactionModalOpen(false);
      setRowSelection({});
      setEditTransaction(null);
    }
  };

  const handleApply = async (
    type: string,
    data: ApplyAndConfirmTransactionDataPayload
  ) => {
    if (type == "apply") {
      await applyTransaction(data);
    } else {
      await applyAndConfirmTransaction(data);
    }
  };

  const applyTransaction = async (
    data: ApplyAndConfirmTransactionDataPayload
  ) => {
    setIsLoading((prev) => ({ ...prev, isApplyingTransaction: true }));
    try {
      const response = await applyTransactionApi(businessId, data);
      if (response && response.success) {
        onSuccess?.(
          "POST",
          "applyTransaction",
          "Transaction Applied SuccessFully"
        );
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while applying Transaction."));
    } finally {
      setIsLoading((prev) => ({ ...prev, isApplyingTransaction: false }));
      getCategoriesCount({});
      fetchTransactions({});
      setRowSelection({});
      setIsEditTransactionModalOpen(false);
      setEditTransaction(null);
    }
  };

  const undoCateogrizedTransaction = async (selectedId: string) => {
    setIsLoading((prev) => ({ ...prev, isundoCategorizedTransaction: true }));
    const result = await undoCategorizedTransactionApi(
      businessId,
      selectedId,
      handleApiError,
      onError
    );
    if (result.success) {
      onSuccess?.(
        "POST",
        "undoCategorizedTransaction",
        result.message || "Transaction undo successfully!"
      );
    }
    setIsLoading((prev) => ({
      ...prev,
      isundoCategorizedTransaction: false,
    }));
    getCategoriesCount({});
    fetchTransactions({});
  };

  const bulkConfirmTransaction = async (selectedrowIds: Transaction[]) => {
    setIsLoading((prev) => ({ ...prev, isbulkConfirmingTransaction: true }));
    let selctedIds = selectedrowIds?.map((item) => item.transactionId) || [];
    try {
      const response = await bulkConfirmTransactionApi(businessId, selctedIds);
      if (response) {
        const failedItems = response.filter((item) => !item.isSuccess);
        if (failedItems.length > 0) {
          const failedIds = failedItems
            .map((item) => item.transactionId)
            .join(", ");
          onError?.(new Error(`Failed to confirm transactions: ${failedIds}`));
        } else {
          onSuccess?.(
            "POST",
            "bulkConfirmTransaction",
            "Transactions confirmed successfully!"
          );
        }
      }
    } catch (error) {
      onError?.(new Error("An error occurred while applying Transaction."));
    } finally {
      setIsLoading((prev) => ({ ...prev, isbulkConfirmingTransaction: false }));
      setRowSelection({});
      getCategoriesCount({});
      fetchTransactions({});
    }
  };

  const excludeTransaction = async (selectedIds: Transaction[]) => {
    setIsLoading((prev) => ({ ...prev, isExludingTransaction: true }));

    const selctedIds = selectedIds?.map((item) => item.transactionId) || [];
    try {
      const response = await excludeTransactionApi(businessId, selctedIds);

      const failedItems = response.filter((item) => !item.isSuccess);

      if (failedItems.length > 0) {
        const failedIds = failedItems
          .map((item) => item.transactionId)
          .join(", ");
        onError?.(new Error(`Failed to exclude transactions: ${failedIds}`));
      } else {
        onSuccess?.(
          "POST",
          "excludeTransaction",
          "Transactions excluded successfully!"
        );
        setRowSelection({});
      }
    } catch (error) {
      onError?.(new Error("An error occurred while excluding Transaction."));
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        isExludingTransaction: false,
      }));
      setRowSelection({});
      getCategoriesCount({});
      fetchTransactions({});
    }
  };

  const handleDownloadTransactions = async () => {
    setIsLoading((prev) => ({
      ...prev,
      isDownloading: true,
    }));
    if (selectedAccountId) {
      // const response = await fetchTransactionsApi({
      //   businessId,
      //   accountId: selectedAccountId,
      //   category: selectedCategory,
      //   term: searchQuery,
      //   toDate: transactionDates.endDate,
      //   fromDate: transactionDates.startDate,
      //   pageNumber: 1,
      //   pageSize: pagination.totalRecords, // fetch all
      // });
      await downloadReport({
        data: transactions,
        fileName: "Transactions",
        format: "excel",
        type: "transactions", // or another type if you want a different format
        selectedCategory: selectedCategory,
      });
      onSuccess?.(
        "DOWNLOAD",
        "fetchTransactions",
        "Transactions downloaded successfully!"
      );

      setIsLoading((prev) => ({
        ...prev,
        isDownloading: false,
      }));
    }
  };

  return (
    <BankingContext.Provider
      value={{
        categories,
        transactions,
        selectedCategory,
        setSelectedCategory,
        searchQuery,
        handleSearch,
        handleSearchClick,
        handleDateChange,
        confirmTransaction,
        isEditTransactionModalOpen,
        handleChangeEditTransactionModal,
        rowSelection,
        setRowSelection,
        loading: isLoading,
        transactionTypeOptions,
        categoryOptions,
        customerVendorOptions,
        handleCustomerInputChange,
        handleApply,
        excludeTransaction,
        bulkConfirmTransaction,
        undoCateogrizedTransaction,
        handlePageSizeChange,
        handlePageChange,
        pagination,
        handleDownloadTransactions,
        editTransaction,
        fetchTransactions,
        handleMatchTransaction,
        modalStates,
        categoriesCount,
        getCategoriesCount,
      }}
    >
      {children}
    </BankingContext.Provider>
  );
};
